import { useMemo, useState, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import * as Sentry from '@sentry/core';
import { useTenantConfig, useTenantConfigStatus } from '@packlink/tenant-config-provider';
import { getBackend, getOptions, i18nInit } from '../utils/i18n.js';
import { getAvailableLanguages } from '../utils/languageUtils.js';
import { TranslationContextValue } from './translationContext.js';
import { DEFAULT_NAMESPACE } from '../config.js';
import { jsx } from '@emotion/react/jsx-runtime';
import i18next from 'i18next';

const DEFAULT_CONFIG = {
  defaultLanguage: 'en-GB',
  availableLanguages: ['en-GB'],
  translationsNamespaces: [DEFAULT_NAMESPACE]
};
const TranslationProvider = _ref => {
  var _useTenantConfig;
  let {
    cdn,
    children,
    application,
    locale,
    debug,
    useSuspense,
    resources,
    onLoad
  } = _ref;
  const translationBackend = useMemo(() => getBackend({
    cdn,
    application
  }), [cdn, application]);
  const getAvailableLanguages$1 = useMemo(() => getAvailableLanguages(cdn, application), [cdn, application]);
  const {
    availableLanguages,
    translationsNamespaces,
    defaultLanguage
  } = (_useTenantConfig = useTenantConfig()) !== null && _useTenantConfig !== void 0 ? _useTenantConfig : DEFAULT_CONFIG;
  const i18nOptions = useMemo(
  // Default fallback for all applications its en-GB
  () => getOptions({
    defaultLanguage,
    locale,
    translationsNamespaces,
    availableLanguages,
    useSuspense,
    debug,
    resources
  }), [defaultLanguage, locale, translationsNamespaces, availableLanguages, useSuspense, debug, resources]);
  const {
    error: tenantError,
    loading: tenantLoading
  } = useTenantConfigStatus() || {};
  const [init, setInit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // If loading its not managed by suspense we need to wait internal loading to avoid repaint with keys and then with translations
  const isLoading = useMemo(() => useSuspense !== undefined && !useSuspense ? loading : false, [loading, useSuspense]);
  useEffect(() => {
    if (!tenantLoading && !tenantError) {
      setLoading(true);
      setError(null);
      i18nInit({
        backend: translationBackend,
        i18nOptions
      }).then(() => {
        var _i18next$language;
        return onLoad && onLoad(((_i18next$language = i18next.language) !== null && _i18next$language !== void 0 ? _i18next$language : i18nOptions.lng).substr(0, 2));
      }).catch(error => {
        Sentry.captureException(error);
        setError(error);
      }).finally(() => setLoading(false));
      setInit(true);
    }
  }, [tenantError, i18nOptions, tenantLoading, translationBackend, onLoad]);

  /**
   * We need the provider because we are using it from library and want to force all of useTranslation or withTranslation
   * to use the same i18n instance - https://react.i18next.com/latest/i18nextprovider#when-to-use
   * */
  return init && !isLoading ? jsx(TranslationContextValue.Provider, {
    value: {
      getAvailableLanguages: getAvailableLanguages$1,
      loading,
      error
    },
    children: jsx(I18nextProvider, {
      i18n: i18next,
      children: children
    }, "i18next-provider")
  }) : null;
};

export { TranslationProvider };
//# sourceMappingURL=translationProvider.js.map
